import ReactECharts from 'echarts-for-react'
import {Button} from 'primereact/button'
import {Column} from 'primereact/column'
import {DataTable} from 'primereact/datatable'
import {Tooltip} from 'primereact/tooltip'
import React, {useCallback, useMemo, useRef} from 'react'
import {ReactComponent as BiRun} from '../../assets/icons/BiRun.svg'
import {ReactComponent as BiTimer} from '../../assets/icons/BiTimer.svg'
import {ReactComponent as BiTrendingUp} from '../../assets/icons/BiTrendingUp.svg'
import {ReactComponent as FaBalanceScale} from '../../assets/icons/FaBalanceScale.svg'
import {ReactComponent as FaClipboardList} from '../../assets/icons/FaClipboardList.svg'
import {ReactComponent as FaPersonFalling} from '../../assets/icons/FaPersonFalling.svg'
import {ReactComponent as FaPersonFallingBurst} from '../../assets/icons/FaPersonFallingBurst.svg'
import {ReactComponent as FaPersonWalking} from '../../assets/icons/FaPersonWalking.svg'
import {ReactComponent as FaPersonWalkingDashedLineArrowRight} from '../../assets/icons/FaPersonWalkingDashedLineArrowRight.svg'
import {ReactComponent as GiFootTrip} from '../../assets/icons/GiFootTrip.svg'
import {ReactComponent as GiFootprint} from '../../assets/icons/GiFootprint.svg'
import {ReactComponent as GiFootsteps} from '../../assets/icons/GiFootsteps.svg'
import {ReactComponent as GiPathDistance} from '../../assets/icons/GiPathDistance.svg'
import {ReactComponent as GiRunningShoe} from '../../assets/icons/GiRunningShoe.svg'
import {ReactComponent as IoFootstepsSharp} from '../../assets/icons/IoFootstepsSharp.svg'
import {ReactComponent as RiPinDistanceLine} from '../../assets/icons/RiPinDistanceLine.svg'

import TranslateUtils from '../../i18n/TranslateUtils'
import Map from '../map/Map'
import {convertKeyToLabel} from '../table-utils/TableUtils'

const HealthStat = () => {
    const {translateCommons, translateKeys} = TranslateUtils()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const data = {
        steps: {
            '2023/08/30': 2000,
            '2023/08/29': 10000,
            '2023/08/28': 2500,
            '2023/08/27': 9000,
            '2023/08/26': 5000,
            '2023/08/25': 9800,
            '2023/08/24': 1600,
        },
        falls: [
            {
                longitude: '-0.127758',
                latitude: '51.507351',
                detectedAt: '2023/08/30',
            },
            {
                longitude: '106.700981',
                latitude: '10.776530',
                detectedAt: '2023/08/28',
            },
            {
                longitude: '126.982283',
                latitude: '37.572032',
                detectedAt: '2023/08/25',
            },
        ],
        activities: [
            {
                label: 'Walking',
                type: 'walking',
                description: 'The number of steps taken this month',
                extraData: [
                    {
                        key: 'totalDistance',
                        icon: () => (
                            <RiPinDistanceLine
                                style={{width: '24px', height: '24px'}}
                            />
                        ),
                        value: `19.5 ${translateCommons('km')}`,
                    },
                    {
                        key: 'totalSteps',
                        icon: () => (
                            <IoFootstepsSharp
                                style={{width: '24px', height: '24px'}}
                            />
                        ),
                        value: `2530 ${translateCommons('steps')}`,
                    },
                    {
                        key: 'timeTaken',
                        icon: () => (
                            <BiTimer style={{width: '24px', height: '24px'}} />
                        ),
                        value: `8 ${translateCommons(
                            'days',
                        )} 16 ${translateCommons('hours')}`,
                    },
                ],
            },
            {
                label: 'Gait Analysis',
                type: 'gaitAnalysis',
                description: 'The walking pattern of the patient',
                extraData: [
                    {
                        key: 'pattern',
                        value: (
                            <div className="flex-column align-items-center gap-2">
                                <div className="flex justify-content-between align-items-center gap-3 mb-2">
                                    <div>{translateCommons('Left')}</div>
                                    <div>
                                        <GiFootprint
                                            style={{
                                                transform: 'rotate(45deg)',
                                                width: '24px',
                                                height: '24px',
                                            }}
                                        />
                                    </div>
                                    <div
                                        className="flex"
                                        style={{
                                            height: '20px',
                                        }}>
                                        <div
                                            style={{
                                                backgroundColor: 'green',
                                                width: '40px',
                                                height: '20px',
                                            }}></div>
                                        <div
                                            style={{
                                                backgroundColor: 'darkcyan',
                                                width: '80px',
                                                height: '20px',
                                            }}></div>
                                        <div
                                            style={{
                                                backgroundColor: 'lightgreen',
                                                width: '60px',
                                                height: '20px',
                                            }}></div>
                                        <div
                                            style={{
                                                backgroundColor: 'blue',
                                                width: '120px',
                                                height: '20px',
                                            }}></div>
                                    </div>
                                </div>
                                <div className="flex justify-content-between align-items-center gap-3">
                                    <div>{translateCommons('Right')}</div>
                                    <div>
                                        <GiFootprint
                                            style={{
                                                transform:
                                                    'scale(1, -1) rotate(45deg)',
                                                width: '24px',
                                                height: '24px',
                                            }}
                                        />
                                    </div>
                                    <div
                                        className="flex"
                                        style={{
                                            height: '20px',
                                        }}>
                                        <div
                                            style={{
                                                backgroundColor: 'green',
                                                width: '50px',
                                                height: '20px',
                                            }}></div>
                                        <div
                                            style={{
                                                backgroundColor: 'darkcyan',
                                                width: '90px',
                                                height: '20px',
                                            }}></div>
                                        <div
                                            style={{
                                                backgroundColor: 'lightgreen',
                                                width: '120px',
                                                height: '20px',
                                            }}></div>
                                        <div
                                            style={{
                                                backgroundColor: 'blue',
                                                width: '40px',
                                                height: '20px',
                                            }}></div>
                                    </div>
                                </div>
                            </div>
                        ),
                    },
                ],
            },
            {
                label: 'Balance',
                type: 'balance',
                description: 'The way the patient walks',
                extraData: [
                    {
                        key: 'totalScore',
                        icon: () => (
                            <FaClipboardList
                                style={{width: '24px', height: '24px'}}
                            />
                        ),
                        value: translateCommons('Good'),
                    },
                    {
                        key: 'pronation',
                        icon: () => (
                            <GiFootprint
                                style={{width: '24px', height: '24px'}}
                            />
                        ),
                        value: translateCommons('Great'),
                    },
                    {
                        key: 'footStrike',
                        icon: () => (
                            <GiRunningShoe
                                style={{width: '24px', height: '24px'}}
                            />
                        ),
                        value: translateCommons('Low'),
                    },
                    {
                        key: 'cadence',
                        icon: () => (
                            <BiRun style={{width: '24px', height: '24px'}} />
                        ),
                        value: translateCommons('Great'),
                    },
                    {
                        key: 'stepLength',
                        icon: () => (
                            <GiFootsteps
                                style={{width: '24px', height: '24px'}}
                            />
                        ),
                        value: translateCommons('Great'),
                    },
                ],
            },
            {
                label: 'FALL Prediction',
                type: 'fallPrediction',
                description:
                    'The prediction of which patients are likely to fall',
                extraData: [
                    {
                        key: 'likelyhood',
                        icon: () => (
                            <BiTrendingUp
                                style={{width: '24px', height: '24px'}}
                            />
                        ),
                        value: translateCommons('High'),
                    },
                ],
            },
        ],
    }

    const sortStepData = useMemo(() => {
        if (!data) {
            return {}
        }
        let stepList = data && data?.steps ? {...data?.steps} : {}

        let stepsData = []
        let labels = []

        for (const key in stepList) {
            labels.push(key)
        }

        labels.sort((a, b) => {
            a = a.split('/').join('')
            b = b.split('/').join('')
            return a.localeCompare(b)
        })

        for (let i = 0; i < labels?.length; i++) {
            stepsData.push(stepList[labels[i]] ? stepList[labels[i]] : 0)
        }

        return {
            data: stepsData,
            labels: labels,
        }
    }, [data])

    const stepsChartTodayOptions = useMemo(() => {
        let stepsDataList =
            sortStepData && sortStepData?.data && sortStepData?.data?.length > 0
                ? [...sortStepData?.data]
                : []

        const stepsTodayData = [
            {
                value: stepsDataList.pop(),
                name: translateCommons('Walking Today'),
                title: {
                    offsetCenter: ['0%', '-30%'],
                },
                detail: {
                    valueAnimation: true,
                    offsetCenter: ['0%', '-10%'],
                },
            },
        ]

        return {
            series: [
                {
                    type: 'gauge',
                    startAngle: 90,
                    endAngle: -270,
                    min: 0,
                    pointer: {
                        show: false,
                    },
                    tooltip: {
                        show: true,
                    },
                    progress: {
                        show: true,
                        overlap: false,
                        roundCap: true,
                        clip: false,
                        itemStyle: {
                            borderWidth: 1,
                            borderColor: '#464646',
                        },
                    },
                    axisLine: {
                        lineStyle: {
                            width: 20,
                        },
                    },
                    splitLine: {
                        show: false,
                        distance: 0,
                        length: 10,
                    },
                    axisTick: {
                        show: false,
                    },
                    axisLabel: {
                        show: false,
                        distance: 50,
                    },
                    data: stepsTodayData,
                    title: {
                        fontSize: '1rem',
                    },
                    detail: {
                        width: 20,
                        height: 10,
                        fontSize: '1rem',
                        color: 'inherit',
                        padding: 20,
                        formatter: `{value} ` + translateCommons('Steps'),
                    },
                },
            ],
        }
    }, [sortStepData, translateCommons])

    const stepsChartInWeekOptions = useMemo(() => {
        const labels =
            sortStepData && sortStepData?.labels
                ? [...sortStepData?.labels]
                : []

        let dataList =
            sortStepData && sortStepData?.data ? [...sortStepData?.data] : []

        if (dataList?.length > 0) {
            dataList = dataList?.map(item => {
                if (item && item >= 10000) {
                    return {
                        value: item,
                        itemStyle: {
                            color: 'green',
                        },
                    }
                } else return item
            })
        }

        return {
            xAxis: {
                type: 'category',
                data: labels,
            },
            yAxis: {
                type: 'value',
            },
            tooltip: {
                show: true,
            },
            series: [
                {
                    data: dataList,
                    type: 'bar',
                },
            ],
        }
    }, [sortStepData])

    const renderColumns = useCallback(() => {
        let shownColumns = [
            {
                key: 'longitude',
            },
            {
                key: 'latitude',
            },
            {
                key: 'detectedAt',
            },
        ]

        return shownColumns?.map((item, idx) => {
            return (
                <Column
                    key={item.key}
                    columnKey={item.key}
                    className="overflow-dot overflow-text-2"
                    field={item.key}
                    header={translateKeys(convertKeyToLabel(item.key))}
                    body={rowData =>
                        rowData ? (
                            <div
                                className="overflow-dot overflow-text-2"
                                style={{width: '100%'}}>
                                {rowData[item.key] ? rowData[item.key] : '-'}
                            </div>
                        ) : (
                            '-'
                        )
                    }
                />
            )
        })
    }, [translateKeys])

    const mapRef = useRef()

    return (
        <React.Fragment>
            <div className="flex flex-column col-12 p-0">
                <div className="font-bold mb-0 text-2xl mb-3">
                    <span className="mr-3">
                        <GiFootTrip style={{width: '24px', height: '24px'}} />
                    </span>
                    {translateCommons('Steps')}
                </div>
                <div className="col-12 flex flex-column sm:flex-column md:flex-row lg:flex-row justify-content-center align-items-center p-0">
                    <div className="col-12 sm:col-12 md:col-4 lg:col-4 p-0">
                        <ReactECharts
                            className="col-12"
                            style={{minHeight: '40vh'}}
                            option={stepsChartTodayOptions}
                        />
                    </div>
                    <div className="col-12 sm:col-12 md:col-8 lg:col-8 p-0">
                        <ReactECharts
                            className="col-12"
                            style={{minHeight: '40vh'}}
                            option={stepsChartInWeekOptions}
                        />
                    </div>
                </div>
                <div className="font-bold mb-0 text-2xl mb-3">
                    <span className="mr-3">
                        <GiPathDistance
                            style={{
                                width: '24px',
                                height: '24px',
                            }}
                        />
                    </span>
                    {translateCommons('Distance and Activity Types')}
                </div>
                <div className="col-12 flex justify-content-center align-items-center p-0  mb-3">
                    <div className="grid justify-content-center align-items-stretch col-12 p-0">
                        {data &&
                            data?.activities &&
                            data?.activities?.length > 0 &&
                            data?.activities.map(item => (
                                <div className="col-6" key={item.type}>
                                    <div
                                        className="flex justify-content-center align-items-center col-12 border-primary border-round border-1 flex flex-column sm:flex-column md:flex-row lg:flex-row h-full"
                                        style={{minHeight: '10vh'}}>
                                        <div className="col-12 sm:col-12 md:col-4 lg:col-4 flex flex-column align-items-center p-0 justify-content-center">
                                            <h3
                                                className="text-md font-bold mr-2"
                                                style={{color: 'darkcyan'}}>
                                                {translateCommons(
                                                    convertKeyToLabel(
                                                        item?.type,
                                                    ),
                                                )}
                                            </h3>
                                            {item?.type === 'walking' ? (
                                                <FaPersonWalking
                                                    style={{
                                                        width: '30px',
                                                        height: '30px',
                                                    }}
                                                />
                                            ) : item?.type ===
                                              'gaitAnalysis' ? (
                                                <FaPersonWalkingDashedLineArrowRight
                                                    style={{
                                                        width: '30px',
                                                        height: '30px',
                                                    }}
                                                />
                                            ) : item?.type === 'balance' ? (
                                                <FaBalanceScale
                                                    style={{
                                                        width: '30px',
                                                        height: '30px',
                                                    }}
                                                />
                                            ) : (
                                                item?.type ===
                                                    'fallPrediction' && (
                                                    <FaPersonFallingBurst
                                                        style={{
                                                            width: '30px',
                                                            height: '30px',
                                                        }}
                                                    />
                                                )
                                            )}
                                        </div>
                                        <div className="col-12 sm:col-12 md:col-8 lg:col-8 flex flex-column text-center text-xl p-0">
                                            <div className="col-12 flex-column align-items-center">
                                                <p className="text-left mr-2 m-0 text-sm sm:text-sm md:text-xl lg:text-xl">
                                                    {translateCommons(
                                                        item?.description,
                                                    )}
                                                </p>
                                                <div className="flex gap-3">
                                                    {item?.extraData?.map(
                                                        childItem => (
                                                            <div
                                                                key={
                                                                    childItem.key
                                                                }
                                                                className="text-md sm:text-md md:text-xl lg:text-xl">
                                                                <Tooltip
                                                                    target={`.childItem-${childItem.key}`}
                                                                    position="top"
                                                                    content={translateCommons(
                                                                        convertKeyToLabel(
                                                                            childItem.key,
                                                                        ),
                                                                    )}
                                                                />
                                                                <div
                                                                    className={`flex align-items-center childItem-${childItem.key} pointer`}>
                                                                    {childItem?.icon && (
                                                                        <b className="text-primary">
                                                                            <span className="mr-2">
                                                                                {childItem.icon()}
                                                                            </span>
                                                                        </b>
                                                                    )}
                                                                    <b>
                                                                        {
                                                                            childItem?.value
                                                                        }
                                                                    </b>
                                                                </div>
                                                            </div>
                                                        ),
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                    </div>
                </div>
                <div className="font-bold mb-0 text-2xl">
                    <span className="mr-3">
                        <FaPersonFalling
                            style={{width: '24px', height: '24px'}}
                        />
                    </span>
                    {translateCommons('FALL Detection')}
                </div>
                <div className="col-12 flex justify-content-center align-items-center">
                    <div className="col-12">
                        <DataTable
                            value={
                                data && data?.falls && data?.falls?.length > 0
                                    ? data?.falls
                                    : []
                            }
                            scrollable
                            resizableColumns
                            rowHover
                            columnResizeMode="fit"
                            reorderableColumns
                            scrollHeight="auto"
                            responsiveLayout="scroll">
                            {renderColumns()}
                            <Column
                                key={'location'}
                                columnKey={'location'}
                                className="overflow-dot overflow-text-2"
                                field={'location'}
                                header={translateKeys(
                                    convertKeyToLabel('location'),
                                )}
                                body={rowData =>
                                    rowData &&
                                    Object.keys(rowData).length > 0 ? (
                                        <Button
                                            className="p-button-rounded"
                                            type="button"
                                            icon="pi pi-map-marker"
                                            aria-label="Cancel"
                                            onClick={() => {
                                                mapRef?.current.showForm({
                                                    longitude:
                                                        rowData?.longitude
                                                            ? rowData?.longitude
                                                            : null,
                                                    latitude: rowData?.latitude
                                                        ? rowData?.latitude
                                                        : null,
                                                })
                                            }}></Button>
                                    ) : (
                                        '-'
                                    )
                                }
                            />
                        </DataTable>
                    </div>
                </div>
            </div>
            <Map ref={mapRef} />
        </React.Fragment>
    )
}

export default HealthStat
